import React, { useEffect, useMemo, useState } from 'react';
import { AdminField } from '../adminShared';
import {
  AdminSectionTitle,
  AdminDevRowMeta,
  AdminVisibilityField,
  btnPrimary,
  btnSecondary,
  inputClass,
  selectClass,
  textareaClass,
} from '../adminUi';
import {
  createGradingDiffFactor,
  createGradingFamily,
  createGradingRole,
  createGradingSpecialization,
  createGradingRoleCluster,
  deleteGradingCalibration,
  deleteGradingDiffFactor,
  deleteGradingFamily,
  deleteGradingQuestion,
  deleteGradingRole,
  deleteGradingSpecialization,
  deleteGradingRoleCluster,
  saveGradingCalibration,
  saveGradingQuestion,
  updateGradingDiffFactor,
  updateGradingFamily,
  updateGradingRole,
  updateGradingSpecialization,
  updateGradingRoleCluster,
} from '../../../../services/gradingService';
import { useGrading } from './GradingContext';
import { GradingContextPath } from './GradingShared';
import type { AnswerDraft, CalibrationAnswerDraft, GradingDrawerState } from './types';
import { formatPath, getCalibrationForFamily, parseGradingScoreInput } from './utils';

function isEntityVisible(value?: boolean) {
  return value !== false;
}

function drawerTitle(state: GradingDrawerState): string {
  if (!state) return '';
  const action = state.mode === 'create' ? 'Добавить' : 'Редактировать';
  const map: Record<string, string> = {
    family: 'Family',
    role_cluster: 'Role cluster',
    specialization: 'Specialization',
    role: 'Role',
    factor: 'диффактор',
    question: 'вопрос',
    calibration: 'калибровку',
  };
  return `${action} ${map[state.type] || ''}`;
}

export default function GradingDrawer() {
  const { drawer, closeDrawer } = useGrading();
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDirty(false);
  }, [drawer]);

  if (!drawer) return null;

  const handleClose = () => {
    if (dirty && !window.confirm('Есть несохранённые изменения. Закрыть?')) return;
    closeDrawer();
  };

  return (
    <>
      <div className="dc-grading-drawer-backdrop" onClick={handleClose} aria-hidden="true" />
      <aside className="dc-grading-drawer" role="dialog" aria-modal="true">
        <header className="dc-grading-drawer-header">
          <div>
            <h2>{drawerTitle(drawer)}</h2>
            <DrawerContextLine state={drawer} />
          </div>
          <button type="button" className="dc-grading-drawer-close" onClick={handleClose} aria-label="Закрыть">
            <i className="fas fa-times" />
          </button>
        </header>

        <div className="dc-grading-drawer-body">
          {drawer.type === 'family' && <FamilyForm state={drawer} onDirty={() => setDirty(true)} onClose={closeDrawer} />}
          {drawer.type === 'role_cluster' && <RoleClusterForm state={drawer} onDirty={() => setDirty(true)} onClose={closeDrawer} />}
          {drawer.type === 'specialization' && <SpecializationForm state={drawer} onDirty={() => setDirty(true)} onClose={closeDrawer} />}
          {drawer.type === 'role' && <RoleForm state={drawer} onDirty={() => setDirty(true)} onClose={closeDrawer} />}
          {drawer.type === 'factor' && <FactorForm state={drawer} onDirty={() => setDirty(true)} onClose={closeDrawer} />}
          {drawer.type === 'question' && <QuestionForm state={drawer} onDirty={() => setDirty(true)} onClose={closeDrawer} />}
          {drawer.type === 'calibration' && <CalibrationForm state={drawer} onDirty={() => setDirty(true)} onClose={closeDrawer} />}
        </div>
      </aside>
    </>
  );
}

function DrawerContextLine({ state }: { state: NonNullable<GradingDrawerState> }) {
  const { families, roleClusters, specializations, roles, factors } = useGrading();
  const path = useMemo(() => {
    if (state.type === 'question' && state.context) {
      const { familyId, roleClusterId, factorId } = state.context;
      return formatPath([
        familyId ? families.find((f) => f.id === familyId)?.name : undefined,
        roleClusterId ? roleClusters.find((s) => s.id === roleClusterId)?.name : undefined,
        factorId ? factors.find((f) => f.id === factorId)?.name : undefined,
      ]);
    }
    if (state.type === 'role_cluster' && state.familyId) {
      return families.find((f) => f.id === state.familyId)?.name || '';
    }
    if (state.type === 'specialization' && state.familyId) {
      return families.find((f) => f.id === state.familyId)?.name || '';
    }
    if (state.type === 'role' && state.id) {
      const r = roles.find((x) => x.id === state.id);
      if (r) {
        const rc = roleClusters.find((s) => s.id === r.role_cluster_id);
        const f = rc ? families.find((x) => x.id === rc.family_id) : undefined;
        const sp = r.specialization_id ? specializations.find((s) => s.id === r.specialization_id) : undefined;
        return formatPath([f?.name, rc?.name, sp?.name]);
      }
    }
    if (state.type === 'calibration' && state.familyId) {
      return families.find((f) => f.id === state.familyId)?.name || '';
    }
    return '';
  }, [state, families, roleClusters, specializations, roles, factors]);

  return <GradingContextPath path={path} />;
}

function DrawerFooter({
  onSave,
  saving,
  onCancel,
  onDelete,
  deleteLabel,
}: {
  onSave: () => void;
  saving?: boolean;
  onCancel: () => void;
  onDelete?: () => void;
  deleteLabel?: string;
}) {
  return (
    <footer className="dc-grading-drawer-footer">
      {onDelete && (
        <button type="button" className="text-sm text-red-600 hover:text-red-800 mr-auto" onClick={onDelete} disabled={saving}>
          {deleteLabel || 'Удалить'}
        </button>
      )}
      <button type="button" className={btnSecondary} onClick={onCancel} disabled={saving}>Отменить</button>
      <button type="button" className={btnPrimary} onClick={onSave} disabled={saving}>
        {saving ? 'Сохранение…' : 'Сохранить'}
      </button>
    </footer>
  );
}

async function runDelete(
  label: string,
  message: string,
  action: () => Promise<void>,
  reload: () => Promise<void>,
  onClose: () => void,
  setError: (s: string) => void,
  setSaving: (b: boolean) => void,
) {
  if (!window.confirm(`${message}\n\nУдалить «${label}»?`)) return;
  setSaving(true);
  setError('');
  try {
    await action();
    await reload();
    onClose();
  } catch (e) {
    setError(e instanceof Error ? e.message : 'Ошибка удаления');
  } finally {
    setSaving(false);
  }
}

function FamilyForm({ state, onDirty, onClose }: { state: Extract<GradingDrawerState, { type: 'family' }>; onDirty: () => void; onClose: () => void }) {
  const { families, reload } = useGrading();
  const existing = state.id ? families.find((f) => f.id === state.id) : undefined;
  const [name, setName] = useState(existing?.name ?? '');
  const [isVisible, setIsVisible] = useState(isEntityVisible(existing?.is_visible));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (!name.trim()) { setError('Укажите название'); return; }
    setSaving(true);
    setError('');
    try {
      if (state.mode === 'create') {
        const created = await createGradingFamily(name.trim());
        if (!isVisible) await updateGradingFamily(created.id, { is_visible: false });
      } else if (state.id) {
        await updateGradingFamily(state.id, { name: name.trim(), is_visible: isVisible });
      }
      await reload();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const remove = state.mode === 'edit' && state.id
    ? () => runDelete(
      name || existing?.name || 'Family',
      'Будут удалены все role cluster, roles, вопросы, диффакторы и калибровка этой Family.',
      () => deleteGradingFamily(state.id!),
      reload,
      onClose,
      setError,
      setSaving,
    )
    : undefined;

  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save(); }}>
      <AdminField label="Название Family" dbField="families.name" required>
        <input className={inputClass} value={name} onChange={(e) => { setName(e.target.value); onDirty(); }} />
      </AdminField>
      <AdminVisibilityField
        checked={isVisible}
        onChange={(next) => { setIsVisible(next); onDirty(); }}
        dbField="families.is_visible"
      />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <DrawerFooter onSave={save} saving={saving} onCancel={onClose} onDelete={remove} />
    </form>
  );
}

function RoleClusterForm({ state, onDirty, onClose }: { state: Extract<GradingDrawerState, { type: 'role_cluster' }>; onDirty: () => void; onClose: () => void }) {
  const { families, roleClusters, reload } = useGrading();
  const existing = state.id ? roleClusters.find((s) => s.id === state.id) : undefined;
  const [familyId, setFamilyId] = useState(String(existing?.family_id ?? state.familyId ?? families[0]?.id ?? ''));
  const [name, setName] = useState(existing?.name ?? '');
  const [isVisible, setIsVisible] = useState(isEntityVisible(existing?.is_visible));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setFamilyId(String(existing?.family_id ?? state.familyId ?? families[0]?.id ?? ''));
    setName(existing?.name ?? '');
    setIsVisible(isEntityVisible(existing?.is_visible));
    setError('');
  }, [state.id, state.mode, state.familyId, existing?.family_id, existing?.name, existing?.is_visible, families]);

  const save = async () => {
    if (!name.trim()) { setError('Укажите название'); return; }
    setSaving(true);
    setError('');
    try {
      if (state.mode === 'create') {
        const created = await createGradingRoleCluster(Number(familyId), name.trim());
        if (!isVisible) await updateGradingRoleCluster(created.id, { is_visible: false });
      } else if (state.id) {
        await updateGradingRoleCluster(state.id, { name: name.trim(), is_visible: isVisible });
      }
      await reload();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const remove = state.mode === 'edit' && state.id
    ? () => runDelete(
      name || existing?.name || 'Role cluster',
      'Будут удалены roles и scoring-вопросы этой Role cluster. Калибровочные ответы, ведущие в неё, тоже удалятся.',
      () => deleteGradingRoleCluster(state.id!),
      reload,
      onClose,
      setError,
      setSaving,
    )
    : undefined;

  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save(); }}>
      <AdminField label="Family" dbField="role_clusters.family_id" required>
        <select className={`${selectClass} dc-select-full`} value={familyId} disabled={!!existing} onChange={(e) => { setFamilyId(e.target.value); onDirty(); }}>
          {families.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </AdminField>
      <AdminField label="Название Role cluster" dbField="role_clusters.name" required>
        <input className={inputClass} value={name} onChange={(e) => { setName(e.target.value); onDirty(); }} />
      </AdminField>
      <AdminVisibilityField
        checked={isVisible}
        onChange={(next) => { setIsVisible(next); onDirty(); }}
        dbField="role_clusters.is_visible"
      />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <DrawerFooter onSave={save} saving={saving} onCancel={onClose} onDelete={remove} />
    </form>
  );
}

function SpecializationForm({ state, onDirty, onClose }: { state: Extract<GradingDrawerState, { type: 'specialization' }>; onDirty: () => void; onClose: () => void }) {
  const { families, specializations, reload } = useGrading();
  const existing = state.id ? specializations.find((s) => s.id === state.id) : undefined;
  const [familyId, setFamilyId] = useState(String(existing?.family_id ?? state.familyId ?? families[0]?.id ?? ''));
  const [name, setName] = useState(existing?.name ?? '');
  const [isVisible, setIsVisible] = useState(isEntityVisible(existing?.is_visible));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (!name.trim()) { setError('Укажите название'); return; }
    setSaving(true);
    setError('');
    try {
      if (state.mode === 'create') {
        const created = await createGradingSpecialization(Number(familyId), name.trim());
        if (!isVisible) await updateGradingSpecialization(created.id, { is_visible: false });
      } else if (state.id) {
        await updateGradingSpecialization(state.id, { name: name.trim(), is_visible: isVisible });
      }
      await reload();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const remove = state.mode === 'edit' && state.id
    ? () => runDelete(
      name || existing?.name || 'Specialization',
      'У связанных roles поле specialization будет сброшено.',
      () => deleteGradingSpecialization(state.id!),
      reload,
      onClose,
      setError,
      setSaving,
    )
    : undefined;

  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save(); }}>
      <AdminField label="Family" dbField="specializations.family_id" required>
        <select className={`${selectClass} dc-select-full`} value={familyId} disabled={!!existing} onChange={(e) => { setFamilyId(e.target.value); onDirty(); }}>
          {families.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </AdminField>
      <AdminField label="Название Specialization" dbField="specializations.name" required>
        <input className={inputClass} value={name} onChange={(e) => { setName(e.target.value); onDirty(); }} />
      </AdminField>
      <AdminVisibilityField
        checked={isVisible}
        onChange={(next) => { setIsVisible(next); onDirty(); }}
        dbField="specializations.is_visible"
      />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <DrawerFooter onSave={save} saving={saving} onCancel={onClose} onDelete={remove} />
    </form>
  );
}

function RoleForm({ state, onDirty, onClose }: { state: Extract<GradingDrawerState, { type: 'role' }>; onDirty: () => void; onClose: () => void }) {
  const { families, roleClusters, specializations, roles, reload } = useGrading();
  const existing = state.id ? roles.find((r) => r.id === state.id) : undefined;
  const initialRc = existing?.role_cluster_id ?? state.roleClusterId;
  const initialRcObj = initialRc ? roleClusters.find((s) => s.id === initialRc) : undefined;

  const [familyId, setFamilyId] = useState(String(initialRcObj?.family_id ?? state.familyId ?? families[0]?.id ?? ''));
  const [roleClusterId, setRoleClusterId] = useState(String(initialRc ?? ''));
  const [specializationId, setSpecializationId] = useState(String(existing?.specialization_id ?? ''));
  const [name, setName] = useState(existing?.name ?? '');
  const [gradeLevel, setGradeLevel] = useState(String(existing?.grade_level ?? ''));
  const [minScore, setMinScore] = useState(existing?.min_score != null ? String(existing.min_score) : '');
  const [maxScore, setMaxScore] = useState(existing?.max_score != null ? String(existing.max_score) : '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [isVisible, setIsVisible] = useState(isEntityVisible(existing?.is_visible));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const filteredRoleClusters = roleClusters.filter((s) => String(s.family_id) === familyId);
  const filteredSpecs = specializations.filter((s) => s.family_id === Number(familyId));

  const save = async () => {
    if (!name.trim() || !roleClusterId || !gradeLevel) {
      setError('Заполните название, Role cluster и Grade');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        roleClusterId: Number(roleClusterId),
        name: name.trim(),
        gradeLevel: Number(gradeLevel),
        specializationId: specializationId ? Number(specializationId) : null,
        description: description.trim() || undefined,
        minScore: minScore === '' ? null : parseGradingScoreInput(minScore),
        maxScore: maxScore === '' ? null : parseGradingScoreInput(maxScore),
        is_visible: isVisible,
      };
      if (state.mode === 'create') await createGradingRole(payload);
      else if (state.id) await updateGradingRole(state.id, payload);
      await reload();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const remove = state.mode === 'edit' && state.id
    ? () => runDelete(
      name || existing?.name || 'Role',
      'Role будет удалена без возможности восстановления.',
      () => deleteGradingRole(state.id!),
      reload,
      onClose,
      setError,
      setSaving,
    )
    : undefined;

  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save(); }}>
      <AdminField label="Family" dbField="role_clusters.family_id" required>
        <select className={`${selectClass} dc-select-full`} value={familyId} onChange={(e) => { setFamilyId(e.target.value); setRoleClusterId(''); onDirty(); }}>
          {families.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </AdminField>
      <AdminField label="Role cluster" dbField="roles.role_cluster_id" required>
        <select className={`${selectClass} dc-select-full`} value={roleClusterId} onChange={(e) => { setRoleClusterId(e.target.value); onDirty(); }}>
          <option value="">Выберите Role cluster</option>
          {filteredRoleClusters.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </AdminField>
      <AdminField label="Specialization (опционально)" dbField="roles.specialization_id">
        <select className={`${selectClass} dc-select-full`} value={specializationId} onChange={(e) => { setSpecializationId(e.target.value); onDirty(); }}>
          <option value="">Без специализации</option>
          {filteredSpecs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </AdminField>
      <AdminField label="Название роли" dbField="roles.name" required>
        <input className={inputClass} value={name} onChange={(e) => { setName(e.target.value); onDirty(); }} />
      </AdminField>
      <div className="grid grid-cols-3 gap-3">
        <AdminField label="Grade" dbField="roles.grade_level" required>
          <input className={inputClass} type="number" value={gradeLevel} onChange={(e) => { setGradeLevel(e.target.value); onDirty(); }} />
        </AdminField>
        <AdminField label="Min score" dbField="roles.min_score" hint="Допускаются дробные значения, напр. 12.5">
          <input className={inputClass} type="number" step="any" inputMode="decimal" value={minScore} onChange={(e) => { setMinScore(e.target.value); onDirty(); }} />
        </AdminField>
        <AdminField label="Max score" dbField="roles.max_score" hint="Допускаются дробные значения">
          <input className={inputClass} type="number" step="any" inputMode="decimal" value={maxScore} onChange={(e) => { setMaxScore(e.target.value); onDirty(); }} />
        </AdminField>
      </div>
      <AdminField label="Описание" dbField="roles.description">
        <textarea className={textareaClass} rows={2} value={description} onChange={(e) => { setDescription(e.target.value); onDirty(); }} />
      </AdminField>
      <AdminVisibilityField
        checked={isVisible}
        onChange={(next) => { setIsVisible(next); onDirty(); }}
        dbField="roles.is_visible"
      />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <DrawerFooter onSave={save} saving={saving} onCancel={onClose} onDelete={remove} />
    </form>
  );
}

function FactorForm({ state, onDirty, onClose }: { state: Extract<GradingDrawerState, { type: 'factor' }>; onDirty: () => void; onClose: () => void }) {
  const { families, factors, reload } = useGrading();
  const existing = state.id ? factors.find((f) => f.id === state.id) : undefined;
  const [familyId, setFamilyId] = useState(String(existing?.family_id ?? state.familyId ?? families[0]?.id ?? ''));
  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [questionHint, setQuestionHint] = useState(existing?.question_hint ?? '');
  const [isVisible, setIsVisible] = useState(isEntityVisible(existing?.is_visible));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (!name.trim()) { setError('Укажите название'); return; }
    setSaving(true);
    setError('');
    try {
      if (state.mode === 'create') {
        await createGradingDiffFactor({
          familyId: Number(familyId),
          name: name.trim(),
          description: description.trim() || undefined,
          questionHint: questionHint.trim() || undefined,
          is_visible: isVisible,
        });
      } else if (state.id) {
        await updateGradingDiffFactor(state.id, {
          name: name.trim(),
          description: description.trim() || null,
          questionHint: questionHint.trim() || null,
          is_visible: isVisible,
        });
      }
      await reload();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const remove = state.mode === 'edit' && state.id
    ? () => runDelete(
      name || existing?.name || 'диффактор',
      'Будут удалены все scoring-вопросы этого фактора.',
      () => deleteGradingDiffFactor(state.id!),
      reload,
      onClose,
      setError,
      setSaving,
    )
    : undefined;

  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save(); }}>
      <AdminField label="Family" dbField="diff_factors.family_id" required>
        <select className={`${selectClass} dc-select-full`} value={familyId} disabled={!!existing} onChange={(e) => { setFamilyId(e.target.value); onDirty(); }}>
          {families.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </AdminField>
      <AdminField label="Название диффактора" dbField="diff_factors.name" required>
        <input className={inputClass} value={name} onChange={(e) => { setName(e.target.value); onDirty(); }} />
      </AdminField>
      <AdminField label="Описание" dbField="diff_factors.description">
        <textarea className={textareaClass} rows={2} value={description} onChange={(e) => { setDescription(e.target.value); onDirty(); }} />
      </AdminField>
      <AdminField label="Подсказка для вопроса" dbField="diff_factors.question_hint">
        <input className={inputClass} value={questionHint} onChange={(e) => { setQuestionHint(e.target.value); onDirty(); }} />
      </AdminField>
      <AdminVisibilityField
        checked={isVisible}
        onChange={(next) => { setIsVisible(next); onDirty(); }}
        dbField="diff_factors.is_visible"
      />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <DrawerFooter onSave={save} saving={saving} onCancel={onClose} onDelete={remove} />
    </form>
  );
}

function QuestionForm({ state, onDirty, onClose }: { state: Extract<GradingDrawerState, { type: 'question' }>; onDirty: () => void; onClose: () => void }) {
  const { families, roleClusters, factors, questions, answers, reload } = useGrading();
  const existing = state.id ? questions.find((q) => q.id === state.id) : undefined;
  const ctx = state.context || {};

  const resolveInitialFamily = () => {
    if (ctx.familyId) return ctx.familyId;
    if (existing) {
      const factor = factors.find((f) => f.id === existing.diff_factor_id);
      return factor?.family_id;
    }
    return families[0]?.id;
  };

  const [familyId, setFamilyId] = useState(String(resolveInitialFamily() ?? ''));
  const [roleClusterId, setRoleClusterId] = useState(String(existing?.role_cluster_id ?? ctx.roleClusterId ?? ''));
  const [factorId, setFactorId] = useState(String(existing?.diff_factor_id ?? ctx.factorId ?? ''));
  const [questionText, setQuestionText] = useState(existing?.question_text ?? '');
  const [questionName, setQuestionName] = useState(existing?.name ?? '');
  const [isVisible, setIsVisible] = useState(isEntityVisible(existing?.is_visible));
  const [answerDrafts, setAnswerDrafts] = useState<AnswerDraft[]>(() => {
    if (existing) {
      return answers.filter((a) => a.question_id === existing.id).map((a) => ({
        id: a.id,
        name: a.name ?? undefined,
        answer_text: a.answer_text,
        matrix_description: a.matrix_description ?? undefined,
        answer_score: String(a.answer_score),
      }));
    }
    return [{ answer_text: '', matrix_description: '', answer_score: '1' }];
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const filteredRoleClusters = roleClusters.filter((s) => String(s.family_id) === familyId);
  const filteredFactors = factors.filter((f) => String(f.family_id) === familyId);

  const scoreRange = useMemo(() => {
    const scores = answerDrafts.map((a) => parseGradingScoreInput(a.answer_score)).filter((n): n is number => n != null);
    if (!scores.length) return null;
    return { min: Math.min(...scores), max: Math.max(...scores) };
  }, [answerDrafts]);

  const addAnswer = () => {
    onDirty();
    setAnswerDrafts((p) => [...p, { answer_text: '', matrix_description: '', answer_score: '0' }]);
  };

  const updateAnswer = (idx: number, patch: Partial<AnswerDraft>) => {
    onDirty();
    setAnswerDrafts((p) => p.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  };

  const removeAnswer = (idx: number) => {
    onDirty();
    setAnswerDrafts((p) => p.filter((_, i) => i !== idx));
  };

  const save = async () => {
    if (!factorId || !roleClusterId || !questionText.trim()) {
      setError('Укажите фактор, role cluster и текст вопроса');
      return;
    }
    const validAnswers = answerDrafts.filter((a) => a.answer_text.trim());
    if (!validAnswers.length) {
      setError('Добавьте хотя бы один ответ');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await saveGradingQuestion({
        mode: state.mode,
        id: state.id,
        diffFactorId: Number(factorId),
        roleClusterId: Number(roleClusterId),
        questionText: questionText.trim(),
        name: questionName.trim() || undefined,
        is_visible: isVisible,
        answers: validAnswers.map((a) => ({
          id: a.id,
          name: a.name,
          answerText: a.answer_text.trim(),
          matrixDescription: a.matrix_description?.trim() || undefined,
          answerScore: parseGradingScoreInput(a.answer_score) ?? 0,
        })),
      });
      await reload();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const remove = state.mode === 'edit' && state.id
    ? () => runDelete(
      questionText.slice(0, 60) || 'вопрос',
      'Вопрос и все его ответы будут удалены.',
      () => deleteGradingQuestion(state.id!),
      reload,
      onClose,
      setError,
      setSaving,
    )
    : undefined;

  return (
    <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); save(); }}>
      <AdminSectionTitle number={1} title="Контекст вопроса" />
      <AdminField label="Family" dbField="questions → diff_factors.family_id">
        <select className={`${selectClass} dc-select-full`} value={familyId} onChange={(e) => { setFamilyId(e.target.value); setRoleClusterId(''); setFactorId(''); onDirty(); }}>
          {families.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </AdminField>
      <AdminField label="Role cluster" dbField="questions.role_cluster_id" required>
        <select className={`${selectClass} dc-select-full`} value={roleClusterId} onChange={(e) => { setRoleClusterId(e.target.value); onDirty(); }}>
          <option value="">—</option>
          {filteredRoleClusters.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </AdminField>
      <AdminField label="Дифференцирующий фактор" dbField="questions.diff_factor_id" required>
        <select className={`${selectClass} dc-select-full`} value={factorId} onChange={(e) => { setFactorId(e.target.value); onDirty(); }}>
          <option value="">—</option>
          {filteredFactors.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </AdminField>
      {state.mode === 'edit' && existing && factorId && String(existing.diff_factor_id) !== factorId && (
        <p className="text-xs text-slate-600 leading-relaxed">
          Смена диффактора сохраняет связи с анкетами: <code>question_id</code> и <code>answer_id</code> не меняются.
        </p>
      )}

      <AdminSectionTitle number={2} title="Текст вопроса" />
      <AdminField label="Название (опционально)" dbField="questions.name">
        <input className={inputClass} value={questionName} onChange={(e) => { setQuestionName(e.target.value); onDirty(); }} />
      </AdminField>
      <AdminField label="Текст вопроса" dbField="questions.question_text" required>
        <textarea className={textareaClass} rows={3} value={questionText} onChange={(e) => { setQuestionText(e.target.value); onDirty(); }} />
      </AdminField>
      <AdminVisibilityField
        checked={isVisible}
        onChange={(next) => { setIsVisible(next); onDirty(); }}
        dbField="questions.is_visible"
      />

      <AdminSectionTitle number={3} title={`Ответы и баллы${scoreRange ? ` (${scoreRange.min} – ${scoreRange.max})` : ''}`} />
      <div className="space-y-3">
        {answerDrafts.map((a, idx) => (
          <div key={idx} className="dc-grading-answer-block">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-slate-500">Ответ {idx + 1}</span>
              <button type="button" className="text-xs text-red-500" onClick={() => removeAnswer(idx)} disabled={answerDrafts.length <= 1}>✕</button>
            </div>
            <AdminField label="Ответ" dbField="answers.answer_text" required>
              <textarea
                className={textareaClass}
                rows={2}
                value={a.answer_text}
                onChange={(e) => updateAnswer(idx, { answer_text: e.target.value })}
                placeholder="Короткий вариант для выбора в анкете"
              />
            </AdminField>
            <AdminField label="Описание для матрицы" dbField="answers.matrix_description">
              <textarea
                className={textareaClass}
                rows={2}
                value={a.matrix_description ?? ''}
                onChange={(e) => updateAnswer(idx, { matrix_description: e.target.value })}
                placeholder="Развёрнутый текст с контекстом диффактора для матрицы грейдинга"
              />
            </AdminField>
            <input className={`${inputClass} mt-2`} type="number" step="any" inputMode="decimal" value={a.answer_score} onChange={(e) => updateAnswer(idx, { answer_score: e.target.value })} placeholder="Баллы (answers.answer_score)" />
          </div>
        ))}
      </div>
      <button type="button" className={btnSecondary} onClick={addAnswer}>+ Добавить ответ</button>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <DrawerFooter onSave={save} saving={saving} onCancel={onClose} onDelete={remove} />
    </form>
  );
}

function CalibrationForm({ state, onDirty, onClose }: { state: Extract<GradingDrawerState, { type: 'calibration' }>; onDirty: () => void; onClose: () => void }) {
  const { roleClusters, calibrationQuestions, calibrationAnswers, reload } = useGrading();
  const familyId = state.familyId;
  const { question, answers: existingAnswers } = getCalibrationForFamily(familyId, calibrationQuestions, calibrationAnswers);
  const familyRoleClusters = roleClusters.filter((s) => s.family_id === familyId);

  const [questionText, setQuestionText] = useState(question?.question_text ?? '');
  const [answerDrafts, setAnswerDrafts] = useState<CalibrationAnswerDraft[]>(() => {
    if (existingAnswers.length) {
      return existingAnswers.map((a) => ({
        id: a.id,
        answer_text: a.answer_text,
        target_role_cluster_id: String(a.target_role_cluster_id),
        sort_order: a.sort_order,
      }));
    }
    return [{ answer_text: '', target_role_cluster_id: String(familyRoleClusters[0]?.id ?? ''), sort_order: 1 }];
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const addAnswer = () => {
    onDirty();
    setAnswerDrafts((p) => [...p, { answer_text: '', target_role_cluster_id: String(familyRoleClusters[0]?.id ?? ''), sort_order: p.length + 1 }]);
  };

  const updateAnswer = (idx: number, patch: Partial<CalibrationAnswerDraft>) => {
    onDirty();
    setAnswerDrafts((p) => p.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  };

  const removeAnswer = (idx: number) => {
    onDirty();
    setAnswerDrafts((p) => p.filter((_, i) => i !== idx));
  };

  const save = async () => {
    if (!questionText.trim()) { setError('Укажите текст вопроса'); return; }
    const valid = answerDrafts.filter((a) => a.answer_text.trim() && a.target_role_cluster_id);
    if (!valid.length) { setError('Добавьте ответы с целевой Role cluster'); return; }
    setSaving(true);
    setError('');
    try {
      await saveGradingCalibration(
        familyId,
        questionText.trim(),
        valid.map((a, idx) => ({
          answerText: a.answer_text.trim(),
          targetRoleClusterId: Number(a.target_role_cluster_id),
          sortOrder: a.sort_order || idx + 1,
        })),
      );
      await reload();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const removeCalibration = question
    ? () => runDelete(
      'калибровку',
      'Калибровочный вопрос и все ответы будут удалены.',
      () => deleteGradingCalibration(familyId),
      reload,
      onClose,
      setError,
      setSaving,
    )
    : undefined;

  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save(); }}>
      <p className="text-xs text-slate-500">Один калибровочный вопрос на Family. Каждый ответ ведёт в Role cluster.</p>
      <AdminField label="Текст вопроса" dbField="calibration_questions.question_text" required>
        <textarea className={textareaClass} rows={3} value={questionText} onChange={(e) => { setQuestionText(e.target.value); onDirty(); }} />
      </AdminField>

      <AdminSectionTitle number={2} title="Ответы → Role cluster" />
      <div className="space-y-3">
        {answerDrafts.map((a, idx) => (
          <div key={idx} className="dc-grading-answer-block">
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="text-xs font-medium text-slate-500">Ответ {idx + 1}</span>
                <AdminDevRowMeta parts={['calibration_answers.answer_text', 'calibration_answers.target_role_cluster_id', 'calibration_answers.sort_order']} />
              </div>
              <button type="button" className="text-xs text-red-500" onClick={() => removeAnswer(idx)} disabled={answerDrafts.length <= 1}>✕</button>
            </div>
            <textarea className={textareaClass} rows={2} value={a.answer_text} onChange={(e) => updateAnswer(idx, { answer_text: e.target.value })} placeholder="Текст ответа" />
            <select className={`${selectClass} dc-select-full mt-2`} value={a.target_role_cluster_id} onChange={(e) => updateAnswer(idx, { target_role_cluster_id: e.target.value })}>
              {familyRoleClusters.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        ))}
      </div>
      <button type="button" className={btnSecondary} onClick={addAnswer}>+ Добавить ответ</button>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <DrawerFooter onSave={save} saving={saving} onCancel={onClose} onDelete={removeCalibration} deleteLabel="Удалить калибровку" />
    </form>
  );
}
